import { Link } from 'react-router-dom';
import { Zap, Github, Twitter, Linkedin, Instagram } from 'lucide-react';

const Footer = ({ settings }) => {
  const socialIcons = {
    github: Github,
    twitter: Twitter,
    linkedin: Linkedin,
    instagram: Instagram,
  };

  // Render logo - use custom logo if available, otherwise default icon with name
  const renderLogo = () => {
    if (settings?.logo_url) {
      return (
        <img 
          src={settings.logo_url} 
          alt={settings?.site_name || 'Logo'} 
          className="h-10 max-w-[180px] object-contain"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      );
    }
    return (
      <>
        <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
          <Zap className="w-6 h-6 text-primary-foreground" />
        </div>
        <span className="font-heading font-bold text-xl">
          {settings?.site_name || 'OQ Technology'}
        </span>
      </>
    );
  };

  return (
    <footer className="bg-[#02040a] border-t border-white/5" data-testid="footer">
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4">
              {renderLogo()}
            </Link>
            <p className="text-muted-foreground mb-6 max-w-sm">
              {settings?.tagline || 'Building digital futures with cutting-edge web solutions.'}
            </p>
            <div className="flex gap-4">
              {settings?.social_links && Object.entries(settings.social_links).map(([platform, url]) => {
                const Icon = socialIcons[platform.toLowerCase()];
                if (!Icon || !url) return null;
                return (
                  <a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-primary/20 hover:text-primary transition-colors"
                    data-testid={`social-${platform}`}
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-3">
              <li><a href="#about" className="text-muted-foreground hover:text-primary transition-colors">About</a></li>
              <li><a href="#services" className="text-muted-foreground hover:text-primary transition-colors">Services</a></li>
              <li><a href="#portfolio" className="text-muted-foreground hover:text-primary transition-colors">Portfolio</a></li>
              <li><a href="#contact" className="text-muted-foreground hover:text-primary transition-colors">Contact</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold mb-4">Contact</h4>
            <ul className="space-y-3 text-muted-foreground">
              {settings?.contact_email && (
                <li>
                  <a href={`mailto:${settings.contact_email}`} className="hover:text-primary transition-colors">
                    {settings.contact_email}
                  </a>
                </li>
              )}
              {settings?.contact_phone && (
                <li>
                  <a href={`tel:${settings.contact_phone}`} className="hover:text-primary transition-colors">
                    {settings.contact_phone}
                  </a>
                </li>
              )}
              {settings?.address && <li>{settings.address}</li>}
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-white/5 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-muted-foreground text-sm">
            {settings?.footer_text || '© 2024 OQ Technology. All rights reserved.'}
          </p>
          <div className="flex gap-6 text-sm text-muted-foreground">
            <a href="#" className="hover:text-primary transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-primary transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
